import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Relatorios.css";
import {
  FiAlertTriangle,
  FiDownload,
  FiFileText,
  FiRefreshCw,
  FiShield,
  FiTool,
} from "react-icons/fi";
import { ClientePageShell } from "../../../components/ClientePageShell";
import {
  ComunicacaoCliente,
  marcarComunicacaoComoLida,
  obterFeedComunicacoesCliente,
  obterRelatoriosCliente,
} from "../../../services/comunicacoesCliente";

const formatReferenceMonth = (date?: string) => {
  if (!date) return "Sem referência";
  const match = date.match(/^(\d{4})-(\d{2})/);
  if (!match) return date;

  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
};

const formatPublishedDate = (date: string) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const NoticeIcon = ({ tipo }: Pick<ComunicacaoCliente, "tipo">) =>
  tipo === "CotaManutencao" ? <FiTool /> : <FiRefreshCw />;

const Relatorios = () => {
  const [cartas, setCartas] = useState<ComunicacaoCliente[]>([]);
  const [avisos, setAvisos] = useState<ComunicacaoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadContent = useCallback(async () => {
    setLoading(true);

    try {
      const [cartasResult, feedResult] = await Promise.all([
        obterRelatoriosCliente(),
        obterFeedComunicacoesCliente(20),
      ]);
      setCartas(cartasResult);
      setAvisos(feedResult.comunicacoes.filter((item) => item.tipo !== "CartaMensal" && item.ativo));
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const unreadReports = useMemo(
    () => cartas.filter((carta) => !carta.lida).length,
    [cartas]
  );

  const markAsRead = async (carta: ComunicacaoCliente) => {
    if (carta.lida) return;

    setCartas((current) =>
      current.map((item) => item.id === carta.id ? { ...item, lida: true } : item)
    );

    try {
      await marcarComunicacaoComoLida(carta.id);
    } catch {
      loadContent();
    }
  };

  return (
    <ClientePageShell
      className="relatorios-page"
      eyebrow="Pesquisa Omex"
      title="Relatórios"
      subtitle="Cartas mensais com leitura de mercado, contexto cripto e análises relacionadas à composição das carteiras."
      actions={
        <button className="cliente-page-button secondary" type="button" onClick={loadContent}>
          <FiRefreshCw />
          Atualizar
        </button>
      }
    >
      {avisos.length > 0 && (
        <section className="relatorios-status-list" aria-label="Status da plataforma">
          {avisos.map((aviso) => (
            <article className="relatorios-status" key={aviso.id}>
              <span><NoticeIcon tipo={aviso.tipo} /></span>
              <div>
                <small>Status da plataforma</small>
                <strong>{aviso.titulo}</strong>
                <p>{aviso.mensagem}</p>
              </div>
            </article>
          ))}
        </section>
      )}

      <section className="relatorios-library">
        <div className="cliente-page-section-header">
          <div>
            <p>Biblioteca</p>
            <h2>Cartas mensais</h2>
          </div>
          <span>{unreadReports ? `${unreadReports} novas` : `${cartas.length} publicadas`}</span>
        </div>

        {loading ? (
          <div className="cliente-page-loading">Carregando relatórios...</div>
        ) : error ? (
          <div className="cliente-page-empty relatorios-error">
            <FiAlertTriangle />
            <p>Não foi possível carregar os relatórios.</p>
            <button className="cliente-page-button" type="button" onClick={loadContent}>
              <FiRefreshCw />
              Tentar novamente
            </button>
          </div>
        ) : cartas.length ? (
          <div className="relatorios-grid">
            {cartas.map((carta, index) => (
              <article className={`relatorio-card ${index === 0 ? "featured" : ""}`} key={carta.id}>
                <div className="relatorio-card-heading">
                  <span className="relatorio-card-icon"><FiFileText /></span>
                  <div>
                    <small>Carta Mensal</small>
                    <strong>{formatReferenceMonth(carta.mesReferencia)}</strong>
                  </div>
                  {!carta.lida && <span className="relatorio-new">Nova</span>}
                </div>
                <div className="relatorio-card-copy">
                  <h3>{carta.titulo}</h3>
                  <p>{carta.mensagem}</p>
                </div>
                <div className="relatorio-card-meta">
                  <span><FiShield /> Omex Capital</span>
                  <time>{formatPublishedDate(carta.criadoEm)}</time>
                </div>
                {carta.urlArquivo && (
                  <a
                    className="cliente-page-button relatorio-card-action"
                    href={carta.urlArquivo}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => markAsRead(carta)}
                  >
                    <FiDownload />
                    Abrir PDF
                  </a>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="cliente-page-empty relatorios-empty">
            <FiFileText />
            <strong>Nenhuma carta mensal publicada.</strong>
            <p>Os próximos relatórios aparecerão aqui.</p>
          </div>
        )}
      </section>
    </ClientePageShell>
  );
};

export default Relatorios;
